import { Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export type CatalogFunnel = { id: string; name: string; is_default: boolean };
export type CatalogStage = { funnel_id: string; slug: string; name: string };
export type CatalogTag = { id: string; name: string };
export type CatalogMember = { id: string; name: string };

export type PartnerApiCatalogData = {
  workspaceId: string;
  funnels: CatalogFunnel[];
  stages: CatalogStage[];
  tags: CatalogTag[];
  members: CatalogMember[];
};

function IdRow({
  label,
  value,
  onCopy,
}: {
  label: string;
  value: string;
  onCopy: (v: string) => void;
}) {
  return (
    <div className="flex items-center gap-2 py-2">
      <div className="min-w-0 flex-1">
        <p className="text-sm text-foreground truncate">{label}</p>
        <code className="block text-[11px] font-mono text-muted-foreground truncate">{value}</code>
      </div>
      <Button type="button" size="sm" variant="ghost" className="shrink-0 h-8 px-2" onClick={() => onCopy(value)}>
        <Copy className="h-3.5 w-3.5" />
        <span className="sr-only">Copiar {label}</span>
      </Button>
    </div>
  );
}

function SectionTrigger({ title, count }: { title: string; count?: number }) {
  return (
    <span className="flex items-center gap-2 text-sm font-medium text-foreground">
      {title}
      {count != null && (
        <span className="text-[12px] font-normal text-muted-foreground tabular-nums">{count}</span>
      )}
    </span>
  );
}

export function buildPartnerCatalogJson(data: PartnerApiCatalogData) {
  return {
    workspace_id: data.workspaceId,
    funnels: data.funnels.map((f) => ({
      id: f.id,
      name: f.name,
      is_default: f.is_default,
      stages: data.stages
        .filter((s) => s.funnel_id === f.id)
        .map((s) => ({ slug: s.slug, name: s.name })),
    })),
    tags: data.tags,
    members: data.members,
  };
}

export function PartnerApiIdCatalog({
  data,
  onCopy,
}: {
  data: PartnerApiCatalogData;
  onCopy: (value: string) => void;
}) {
  const json = JSON.stringify(buildPartnerCatalogJson(data), null, 2);

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-sm font-semibold text-foreground">IDs deste workspace</h4>
        <Button type="button" size="sm" variant="outline" onClick={() => onCopy(json)}>
          <Copy className="h-3.5 w-3.5 mr-1.5" />
          Copiar JSON
        </Button>
      </div>

      <Accordion type="multiple" className="w-full rounded-lg border border-border px-3">
        <AccordionItem value="workspace" className="border-b">
          <AccordionTrigger className="py-3 hover:no-underline">
            <SectionTrigger title="Workspace" />
          </AccordionTrigger>
          <AccordionContent>
            <IdRow label="workspace_id" value={data.workspaceId} onCopy={onCopy} />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="funnels" className="border-b">
          <AccordionTrigger className="py-3 hover:no-underline">
            <SectionTrigger title="Funis" count={data.funnels.length} />
          </AccordionTrigger>
          <AccordionContent>
            {data.funnels.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nenhum funil.</p>
            ) : (
              <Accordion type="multiple" className="w-full">
                {data.funnels.map((funnel) => {
                  const stages = data.stages.filter((s) => s.funnel_id === funnel.id);
                  return (
                    <AccordionItem key={funnel.id} value={funnel.id} className="border-b last:border-0">
                      <AccordionTrigger className="py-2.5 hover:no-underline text-left">
                        <SectionTrigger
                          title={funnel.is_default ? `${funnel.name} (padrão)` : funnel.name}
                          count={stages.length}
                        />
                      </AccordionTrigger>
                      <AccordionContent>
                        <IdRow label="funnel_id" value={funnel.id} onCopy={onCopy} />
                        <Accordion type="multiple" className="w-full">
                          <AccordionItem value={`${funnel.id}-stages`} className="border-0">
                            <AccordionTrigger className="py-2 hover:no-underline">
                              <SectionTrigger title="Colunas" count={stages.length} />
                            </AccordionTrigger>
                            <AccordionContent>
                              {stages.length === 0 ? (
                                <p className="text-sm text-muted-foreground">Sem colunas.</p>
                              ) : (
                                stages.map((stage) => (
                                  <IdRow
                                    key={stage.slug}
                                    label={stage.name}
                                    value={stage.slug}
                                    onCopy={onCopy}
                                  />
                                ))
                              )}
                            </AccordionContent>
                          </AccordionItem>
                        </Accordion>
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            )}
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="tags" className="border-b">
          <AccordionTrigger className="py-3 hover:no-underline">
            <SectionTrigger title="Tags" count={data.tags.length} />
          </AccordionTrigger>
          <AccordionContent>
            {data.tags.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nenhuma tag.</p>
            ) : (
              data.tags.map((tag) => (
                <IdRow key={tag.id} label={tag.name} value={tag.id} onCopy={onCopy} />
              ))
            )}
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="members" className="border-b-0">
          <AccordionTrigger className="py-3 hover:no-underline">
            <SectionTrigger title="Responsáveis" count={data.members.length} />
          </AccordionTrigger>
          <AccordionContent>
            {data.members.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nenhum membro.</p>
            ) : (
              data.members.map((member) => (
                <IdRow key={member.id} label={member.name} value={member.id} onCopy={onCopy} />
              ))
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </section>
  );
}
